import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SubscriptionService } from '../../api/generated/services/SubscriptionService';
import { ProductsService } from '../../api/generated/services/ProductsService';
import type { SubscriptionView } from '../../api/generated/models/SubscriptionView';
import type { ProductView } from '../../api/generated/models/ProductView';
import { setError } from './errorSlice';
import { addNotification } from './notificationSlice';
import { startLoading, stopLoading } from './loadingSlice';
import { helper } from '../../utils/helper';
import { isDataSourceConfigured } from '../../utils/dataSourceHelpers';
import type { RootState } from '../store';
import { fetchContentMasterWorkspaceConfig } from './contentMasterSlice';

// Renamed from License to Subscription but keeping slice name for compatibility
interface LicenseState {
  products: ProductView[];
  // freeOrTrialProducts removed - no longer in API
  userLicenses: SubscriptionView[];
  items: SubscriptionView[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  isLoaded: boolean;
  isPaymentPending: boolean;
  pendingProductId: string | null;
  licensesCountBeforePayment: number;
}

const initialState: LicenseState = {
  products: [],
  userLicenses: [],
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
  isLoaded: false,
  isPaymentPending: false,
  pendingProductId: null,
  licensesCountBeforePayment: 0,
};

export const fetchProducts = createAsyncThunk(
  'license/fetchProducts',
  async (pluginName: string | undefined, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ProductsService.getAppProductList(pluginName);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch products';
      dispatch(setError(errorMessage));

      if (err instanceof Error) {
        if (helper.isNotFoundError(err)) {
          return [];
        }

        dispatch(addNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage,
        }));
      }
      
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

interface FetchUserLicensesArg {
  page: number;
  pageSize: number;
  silent?: boolean;
}

interface FetchUserLicensesResult {
  items: SubscriptionView[];
  total: number;
  page: number;
  pageSize: number;
}

export const fetchUserLicenses = createAsyncThunk<
  FetchUserLicensesResult,
  FetchUserLicensesArg,
  { state: RootState }
>(
  'license/fetchUserLicenses',
  async ({ page, pageSize, silent = false }, { dispatch, getState }) => {
    if (!silent) {
      dispatch(startLoading());
    }
    try {
      const subscriptions = await SubscriptionService.getAppSubscriptionList();

      const hasActiveContentMaster = subscriptions.some(
        (s) => s.pluginName === 'content_master' && helper.isSubscriptionActive(s)
      );
      const root = getState();
      const dsReady = isDataSourceConfigured(root.dataSource.data);
      const workspaceMissing = root.contentMaster.workspaceConfig == null;
      if (hasActiveContentMaster && dsReady && workspaceMissing) {
        await dispatch(fetchContentMasterWorkspaceConfig(undefined));
      }

      return {
        items: subscriptions,
        total: subscriptions.length,
        page,
        pageSize
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscriptions';
      dispatch(setError(errorMessage));

      if (err instanceof Error) {
        if (helper.isNotFoundError(err)) {
          return {
            items: [],
            total: 0,
            page,
            pageSize
          };
        }

        dispatch(addNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage
        }));
      }

      throw err;
    } finally {
      if (!silent) {
        dispatch(stopLoading());
      }
    }
  },
  {
    // Drop a duplicate dispatch while a request is already in flight.
    // Bootstrap, withLicenseCheck and the per-page paging share this thunk;
    // without the guard, they could collide on first render.
    condition: (_, { getState }) => !getState().licenses.loading,
  }
);

export const checkSubscription = createAsyncThunk(
  'license/checkSubscription',
  async (pluginName: string, { dispatch }) => {
    try {
      const response = await SubscriptionService.getAppSubscriptionCheck(pluginName);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check subscription';
      dispatch(setError(errorMessage));
      throw err;
    }
  }
);

// Alias for backward compatibility
export const fetchLicenses = fetchUserLicenses;

const licenseSlice = createSlice({
  name: 'license',
  initialState,
  reducers: {
    setPaymentPending: (state, action: { payload: { productId: string; licensesCount: number } }) => {
      state.isPaymentPending = true;
      state.pendingProductId = action.payload.productId;
      state.licensesCountBeforePayment = action.payload.licensesCount;
    },
    clearPaymentPending: (state) => {
      state.isPaymentPending = false;
      state.pendingProductId = null;
      state.licensesCountBeforePayment = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.isLoaded = true;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      // Fetch User Subscriptions (Licenses)
      .addCase(fetchUserLicenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserLicenses.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.isLoaded = true;
      })
      .addCase(fetchUserLicenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch subscriptions';
      });
  },
});

export const { setPaymentPending, clearPaymentPending } = licenseSlice.actions;
export default licenseSlice.reducer;
